import { Select } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

import { NotificationPosition } from "@superblocksteam/shared";

import {
  useGetAutoTopupConfigQuery,
  useManualTopupChargeMutation,
  useUpdateAutoTopupConfigMutation,
} from "store/slices/reduxApi/billing";
import {
  sendSuccessUINotification,
  sendWarningUINotification,
} from "utils/notification";

import {
  AutoTopUpFormFields,
  type AutoFormState,
  autoFormEqual,
  autoFormFromServer,
  formatUsd,
  isWholeNumber,
  validateAutoForm,
} from "./AutoTopUpForm";
import { MANUAL_TOPUP_CACHE_KEY } from "./AutoTopUpPanel";

const PRESET_AMOUNTS = [50, 100, 250, 500, 1000, 2500] as const;
const CUSTOM_OPTION = "custom" as const;
const MIN_CUSTOM = 20;
const MAX_CUSTOM = 9999;

type AmountOption = (typeof PRESET_AMOUNTS)[number] | typeof CUSTOM_OPTION;

type Props = {
  /** Called to return to the Plan view (back chevron + Cancel button). */
  onBack: () => void;
  /** Called when modal should fully close (× icon, post-charge success). */
  onClose: () => void;
};

/**
 * "One-time refill" body for the Manage Plan modal (formerly "Top up mode" /
 * "Refill credits"). The modal-frame title and the entry-point button on
 * Manage Plan both read "One-time refill" so the action and destination
 * names align — admin clicks "One-time refill" and lands on a view called
 * "One-time refill."
 *
 * Renders inside the existing modal frame (same `<Modal>` antd instance,
 * no nested modal — that flickers because the dim overlay doubles up).
 * Shows two stacked subsections, each with its own header + explainer:
 *
 *   1. One-time refill (top): one-time top-up — Amount select + pricing
 *      card with "Same rate" pill
 *   2. Automatic credit refills (below): switch + 3 stacked field rows
 *      with inline `= $X.XX` preview directly under each charge-driving
 *      input
 *
 * Save model — decoupled:
 *   - The bottom-right primary "Refill N credits · $X.XX" commits the manual
 *     charge only. It never persists auto-refill preferences as a side effect.
 *   - Auto-refill settings are saved independently via the "Save automatic
 *     refill settings" button on the bottom-LEFT of the same footer, which
 *     appears when the auto form is dirty. Admins explicitly opt into
 *     persisting preference changes — no surprise side-effects from the
 *     charge button.
 *   - Footer split: section-scoped Save on the left, modal-scoped Cancel +
 *     Refill primary on the right, separated by `flex-1` spacer. Hierarchy
 *     reads via alignment — the two stakes (reversible save vs irreversible
 *     Stripe charge) are visually distinct without being arbitrary stacked
 *     button rows.
 *   - Save uses the white-with-shadow secondary style; Refill is the only
 *     black primary, so visual hierarchy is unambiguous.
 */
export function TopUpView({ onBack, onClose }: Props) {
  const { data, isLoading } = useGetAutoTopupConfigQuery();
  const [manualCharge, { isLoading: isCharging }] =
    useManualTopupChargeMutation({ fixedCacheKey: MANUAL_TOPUP_CACHE_KEY });
  const [updateAutoConfig, { isLoading: isSavingAuto }] =
    useUpdateAutoTopupConfigMutation();

  const [selected, setSelected] = useState<AmountOption>(100);
  const [customRaw, setCustomRaw] = useState("");
  const [chargeError, setChargeError] = useState<string | null>(null);
  // Timestamp of the most recent auto-refill PATCH success. Drives the
  // "✓ Saved" green chip swap-in and auto-clears 2s later so the slot
  // reverts to "hidden when clean". Tied to a timestamp (not a boolean)
  // so rapid re-saves restart the timer without leaking a stale clear.
  const [autoSavedAt, setAutoSavedAt] = useState<number | null>(null);
  // Progressive label state for the in-flight Refill primary:
  //   stage 0 (immediate)  → "Refilling N credits…"
  //   stage 1 (after 3s)   → "Confirming with Stripe…"
  //   stage 2 (after 7s)   → "Almost done…"
  // Pure UI feedback — keyed on `isCharging` (NOT `inflight`), so a save
  // of auto-refill settings never triggers the progressive copy on the
  // unrelated Refill primary. Resets to 0 when isCharging flips off.
  const [chargeStage, setChargeStage] = useState<0 | 1 | 2>(0);

  const [autoForm, setAutoForm] = useState<AutoFormState>({
    enabled: false,
    packCredits: "",
    monthlyCreditCap: "",
    triggerBufferCredits: "",
  });

  // Sync auto form from server every time `data` changes (mount + cache
  // refresh). Discards in-flight edits — acceptable since the server-side
  // change-detection (e.g. webhook updating last_disabled_reason) is rare
  // while this view is open.
  useEffect(() => {
    if (data) setAutoForm(autoFormFromServer(data));
  }, [data]);

  // Drive the in-flight progressive label on the Refill primary. Timers
  // are cleared whenever isCharging flips off (success, failure, or
  // unmount) so the stage never lingers across charges.
  useEffect(() => {
    if (!isCharging) {
      setChargeStage(0);
      return;
    }
    setChargeStage(0);
    const t1 = window.setTimeout(() => setChargeStage(1), 3000);
    const t2 = window.setTimeout(() => setChargeStage(2), 7000);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isCharging]);

  const customParsed = Number(customRaw);
  const customIsWhole =
    customRaw.trim() !== "" &&
    Number.isFinite(customParsed) &&
    Number.isInteger(customParsed);
  const customError: string | null = useMemo(() => {
    if (selected !== CUSTOM_OPTION) return null;
    if (customRaw.trim() === "") return "Enter an amount";
    if (!customIsWhole) return "Whole number required";
    if (customParsed < MIN_CUSTOM)
      return `Minimum ${MIN_CUSTOM.toLocaleString()} credits`;
    if (customParsed > MAX_CUSTOM)
      return `Maximum ${MAX_CUSTOM.toLocaleString()} credits`;
    return null;
  }, [selected, customRaw, customIsWhole, customParsed]);

  const credits: number | null =
    selected === CUSTOM_OPTION
      ? customError === null
        ? customParsed
        : null
      : selected;

  const unitAmountCents = data?.unit_amount_cents ?? 125;
  const totalCents = credits == null ? null : credits * unitAmountCents;

  const autoErrors = useMemo(() => validateAutoForm(autoForm), [autoForm]);
  const autoHasErrors = Object.keys(autoErrors).length > 0;

  // Dirty if the user has edited any auto field relative to the server snapshot.
  const autoDirty = useMemo(() => {
    if (!data) return false;
    return !autoFormEqual(autoFormFromServer(data), autoForm);
  }, [data, autoForm]);

  const inflight = isCharging || isSavingAuto;
  const hasDefaultPaymentMethod = data?.has_default_payment_method ?? false;
  // Mirrors CreditRefillsCard: when the server reports the refill flow as
  // unavailable, surface the categorical reason as a tooltip on the
  // disabled auto-refill toggle so admins know WHY the control is locked
  // (most common on POC: no_payment_method until they add a card).
  const autoUnavailableTooltip = useMemo<string | null>(() => {
    if (!data || data.available !== false) return null;
    switch (data.unavailable_reason) {
      case "trialing":
        return "Refills become available after your trial converts to a paid subscription.";
      case "canceled":
        return "Refills are unavailable while your subscription is canceled.";
      case "canceling":
        return "Refills are unavailable — your subscription will end soon.";
      case "feature_disabled":
        return "Refills are temporarily disabled for your organization.";
      case "plan_ineligible":
        return "Refills aren't available on your current plan.";
      case "no_payment_method":
        return "Add a credit card on the Usage page to enable refills.";
      default:
        return "Refills aren't available right now.";
    }
  }, [data]);
  const primaryDisabled =
    inflight ||
    credits == null ||
    !hasDefaultPaymentMethod ||
    (autoForm.enabled && autoHasErrors);

  const handlePrimary = useCallback(async () => {
    if (primaryDisabled || credits == null) return;
    setChargeError(null);

    // Single-click commit. The pricing card surfaces the dollar total
    // above, and the modal's Cancel button is the out — a confirm step on
    // top of those just slows admins down without adding meaningful safety.
    // Charge only — the "Save automatic refill settings" inline button is
    // the only path that persists auto-refill preferences, so admin intent
    // is always explicit.
    try {
      const result = await manualCharge({
        credits,
        idempotencyKey:
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`,
      }).unwrap();
      if (result.status === "pending_authentication") {
        setChargeError(
          "Your card needs additional verification (3D Secure). Open the billing portal to complete the charge.",
        );
        return;
      }
      if (result.status !== "succeeded") {
        setChargeError(
          result.failureMessage ?? "The charge could not be completed.",
        );
        return;
      }
    } catch (err: unknown) {
      const errData = (err as { data?: { error?: { message?: string } } }).data;
      setChargeError(
        errData?.error?.message ?? "The charge could not be completed.",
      );
      return;
    }

    // Charge succeeded — top-placement toast confirms to the admin. The
    // toast persists 4.5s and lives at top:46 so it stays visible after
    // the modal hard-closes below.
    if (totalCents != null) {
      sendSuccessUINotification({
        message: `${credits.toLocaleString()} credits added`,
        description: `Charged ${formatUsd(totalCents)} to your card on file.`,
        placement: NotificationPosition.top,
      });
    }

    // Hard-close the entire Manage Plan modal — afterClose resets
    // modalView to "manage" so the next open lands on the plan view, not
    // on a stale topup view.
    onClose();
  }, [primaryDisabled, credits, totalCents, manualCharge, onClose]);

  const handleSaveAutorefill = useCallback(async () => {
    if (isSavingAuto || autoHasErrors || !autoDirty) return;
    try {
      await updateAutoConfig({
        enabled: autoForm.enabled,
        packCredits: isWholeNumber(autoForm.packCredits)
          ? Number(autoForm.packCredits)
          : null,
        monthlyCreditCap: isWholeNumber(autoForm.monthlyCreditCap)
          ? Number(autoForm.monthlyCreditCap)
          : null,
        triggerBufferCredits: isWholeNumber(autoForm.triggerBufferCredits)
          ? Number(autoForm.triggerBufferCredits)
          : null,
      }).unwrap();
      setAutoSavedAt(Date.now());
      sendSuccessUINotification({
        message: autoForm.enabled
          ? "Automatic refills updated"
          : "Automatic refills turned off",
        placement: NotificationPosition.top,
      });
    } catch {
      sendWarningUINotification({
        message: "Couldn't save automatic refill settings",
        description: "Please try again.",
        placement: NotificationPosition.top,
      });
    }
  }, [
    isSavingAuto,
    autoHasErrors,
    autoDirty,
    updateAutoConfig,
    autoForm.enabled,
    autoForm.packCredits,
    autoForm.monthlyCreditCap,
    autoForm.triggerBufferCredits,
  ]);

  // Auto-clear the "✓ Saved" green chip after 2s so the slot reverts to
  // its hidden state once the form is no longer dirty. Tied to the
  // timestamp so rapid re-saves restart the timer cleanly.
  useEffect(() => {
    if (autoSavedAt == null) return;
    const t = setTimeout(() => setAutoSavedAt(null), 2000);
    return () => clearTimeout(t);
  }, [autoSavedAt]);

  // The primary action commits the manual charge only; auto-refill settings
  // are saved independently via the footer-left "Save automatic refill
  // settings" button, so the label never advertises a merged save. Dollar
  // total is baked into the idle label (replacing the prior confirm step)
  // so admins see the exact charge amount on the button itself before
  // clicking.
  //
  // In-flight label is keyed on `isCharging` (not `inflight`) so a save of
  // auto-refill settings doesn't mislead the admin into thinking the
  // Refill primary is processing — the Save flow has its own button +
  // status chip on the left side of the footer. While `isSavingAuto` is
  // true the bottom primary is just disabled (via `primaryDisabled`)
  // with the idle label.
  const chargingLabel =
    chargeStage === 0
      ? `Refilling ${(credits ?? 0).toLocaleString()} credits…`
      : chargeStage === 1
        ? "Confirming with Stripe…"
        : "Almost done…";
  const primaryLabel = isCharging
    ? chargingLabel
    : credits != null && totalCents != null
      ? `Refill ${credits.toLocaleString()} credits · ${formatUsd(totalCents)}`
      : "Refill credits";

  return (
    <div
      data-test="manage-plan-topup-view"
      className="flex max-h-[90vh] flex-col overflow-hidden bg-white"
    >
      {/* Header — back chevron + title + close */}
      <div className="flex shrink-0 items-center gap-2 border-b border-[#e8eaed] px-6 py-4">
        <button
          type="button"
          aria-label="Back to plan view"
          onClick={onBack}
          disabled={inflight}
          className="-ml-1 flex h-7 w-7 cursor-pointer items-center justify-center rounded border-0 bg-transparent text-[#6c7689] transition hover:bg-[#f3f4f6] hover:text-[#1f2633] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="10,3 5,8 10,13" />
          </svg>
        </button>
        <h2 className="!m-0 flex-1 text-[15px] font-semibold leading-5 tracking-[-0.3px] text-[#1f2633]">
          One-time refill
        </h2>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="flex cursor-pointer items-center justify-center rounded p-1 border-0 bg-transparent text-[#6c7689] transition hover:bg-[#f3f4f6]"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 4 4 12M4 4l8 8" />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
        {isLoading || !data ? (
          <div className="text-xs text-[#6c7689]">Loading…</div>
        ) : (
          <>
            {/* ── One-time refill subsection ───────────────────────── */}
            <div className="flex flex-col gap-1">
              <h3 className="!m-0 text-[14px] font-semibold leading-5 text-[#1f2633]">
                One-time refill
              </h3>
              <p className="m-0 text-xs leading-[1.45] text-[#6c7689]">
                Credits are added to your shared pool immediately and expire at
                the end of the next billing cycle.
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-[11px] text-[#6c7689]">Amount</span>
              <Select<AmountOption>
                data-test="topup-view-amount"
                value={selected}
                onChange={(v) => setSelected(v)}
                disabled={inflight}
                options={[
                  ...PRESET_AMOUNTS.map((n) => ({
                    value: n,
                    label: `${n.toLocaleString()} credits`,
                  })),
                  { value: CUSTOM_OPTION, label: "Custom amount" },
                ]}
                style={{ width: "100%" }}
              />
            </div>

            {selected === CUSTOM_OPTION ? (
              <div className="flex items-center gap-2">
                <input
                  data-test="topup-view-custom-input"
                  type="number"
                  min={MIN_CUSTOM}
                  max={MAX_CUSTOM}
                  step={1}
                  value={customRaw}
                  onChange={(e) => setCustomRaw(e.target.value)}
                  onFocus={(e) => e.currentTarget.select()}
                  disabled={inflight}
                  autoFocus
                  className={`h-8 w-[130px] rounded border px-2.5 text-[13px] ${
                    customError ? "border-[#b91c1c]" : "border-[#e8eaed]"
                  }`}
                />
                <span
                  className={`text-[11px] ${
                    customError ? "text-[#b91c1c]" : "text-[#6c7689]"
                  }`}
                >
                  {customError
                    ? customError
                    : `credits · ${MIN_CUSTOM.toLocaleString()} – ${MAX_CUSTOM.toLocaleString()}`}
                </span>
              </div>
            ) : null}

            {/* Pricing card */}
            <div className="flex flex-col gap-1 rounded-md bg-[#f9fafb] px-3 py-2.5 text-xs">
              <PricingRow
                left="Unit price"
                right={`${formatUsd(unitAmountCents)} / credit`}
              />
              {credits != null ? (
                <PricingRow
                  left={`${credits.toLocaleString()} credits`}
                  right={formatUsd(credits * unitAmountCents)}
                />
              ) : null}
              {totalCents != null ? (
                <PricingRow
                  total
                  left="Total today"
                  right={formatUsd(totalCents)}
                />
              ) : null}
              {/* No comparison pill: refill rate currently always matches
                  the Org Credit Pack rate (server doesn't yet expose a
                  separate per-credit subscription rate), so a "Same rate"
                  badge would just add noise. When the server starts
                  returning a distinct rate we'll re-add the pill here for
                  the higher/lower cases only. */}
            </div>

            <hr className="my-1 h-px border-0 border-t border-[#e8eaed]" />

            {/* ── Automatic credit refills subsection ────────────────
                 Shared with /spending's CreditRefillsCard (single source
                 of truth for the field grid + inline pricing). The inline
                 Save button here is TopUpView-specific so admins can save
                 without buying credits in the same click. */}
            <h3 className="!m-0 text-[14px] font-semibold leading-5 text-[#1f2633]">
              Automatic credit refills
            </h3>

            <AutoTopUpFormFields
              form={autoForm}
              onFormChange={setAutoForm}
              errors={autoErrors}
              unitAmountCents={unitAmountCents}
              hasDefaultPaymentMethod={hasDefaultPaymentMethod}
              disabled={inflight}
              disabledTooltip={autoUnavailableTooltip}
              // switchRowSlot intentionally not passed — the inline Save
              // button used to live next to the toggle, but it pushed the
              // toggle label "Automatically purchase more credits when
              // running low (Recommended)" onto two lines and competed with
              // the toggle for visual priority. The Save button now lives
              // in the modal footer, left-aligned, opposite the
              // Cancel + Refill primary cluster (see below). Same state
              // machine — visible only when dirty or just-saved, flips
              // green on success, hidden when clean.
            />

            {chargeError ? (
              <div
                data-test="topup-view-charge-error"
                className="rounded-md bg-[#fde8e8] px-3 py-2 text-xs text-[#991b1b]"
              >
                {chargeError}
              </div>
            ) : null}

            {!hasDefaultPaymentMethod ? (
              <div className="rounded-md bg-[#dbeafe] px-3 py-2 text-xs text-[#1e3a8a]">
                Add a payment method in the billing portal before buying
                credits.
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Footer — single right-aligned cluster: Cancel, optional Save,
          Refill primary. Save sits BETWEEN Cancel and Refill so it reads as
          a sibling commit (section-scoped vs modal-scoped) rather than a
          drift-off-to-the-left orphan. The top divider is hidden when the
          Save button isn't visible — without Save, Cancel + Refill are a
          single cluster that doesn't need a section break above them. */}
      <div
        className={`flex shrink-0 items-center justify-end gap-2 bg-[#f9fafb] px-6 py-3 ${
          autoDirty || autoSavedAt != null ? "border-t border-[#e8eaed]" : ""
        }`}
      >
        <button
          type="button"
          onClick={onBack}
          disabled={inflight}
          className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-[#e8eaed] bg-white px-3 text-sm font-medium text-[#454d5f] transition hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>
        {autoDirty || autoSavedAt != null ? (
          <button
            type="button"
            data-test="topup-view-save-autorefill"
            onClick={handleSaveAutorefill}
            disabled={isSavingAuto || autoHasErrors || !autoDirty}
            className={
              autoSavedAt != null
                ? "inline-flex h-9 items-center gap-1 rounded-md bg-[#d1fae5] px-3 text-sm font-medium text-[#047857]"
                : "inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-[#e8eaed] bg-white px-3 text-sm font-medium text-[#1f2633] shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-50"
            }
          >
            {autoSavedAt != null
              ? "✓ Saved"
              : isSavingAuto
                ? "Saving…"
                : "Save automatic refill settings"}
          </button>
        ) : null}
        <button
          type="button"
          data-test="topup-view-primary"
          onClick={handlePrimary}
          disabled={primaryDisabled}
          className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md bg-black px-4 text-sm font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {primaryLabel}
        </button>
      </div>
    </div>
  );
}

function PricingRow({
  left,
  right,
  total = false,
}: {
  left: string;
  right: string;
  total?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between ${
        total
          ? "mt-0.5 border-t border-[#f1f3f5] pt-1.5 font-semibold text-[#1f2633]"
          : "text-[#6c7689]"
      }`}
    >
      <span>{left}</span>
      <span className="text-[#1f2633] tabular-nums">{right}</span>
    </div>
  );
}
